import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StandardAppLayout } from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import {
    engineErrorMessage,
    asEngineViolations,
    type EngineError,
    type SchemaViolation,
} from 'src/shared/state/api-slices/engineErrorMessage';
import {
    useGetStacCollectionQuery,
    useUpdateStacCollectionMutation,
    type StacCollection,
} from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import CollectionForm from './components/collection-form/CollectionForm';
import DocumentModes from './components/document-editor/DocumentModes';
import { useJsonHatch } from './components/document-editor/useJsonHatch';
import { formFromCollection, toCollectionDocument } from './domain/documents/stacCollectionDocument';

type LoadedCollectionEditProps = {
    collection: StacCollection;
    isSaving: boolean;
    submitError?: string;
    submitViolations?: SchemaViolation[];
    onSave: (collection: StacCollection) => void;
    onCancel: () => void;
};

/**
 * The edit itself, once the dataset has arrived.
 *
 * Its own component because the JSON hatch holds state, and state cannot be held behind a
 * loading branch — the hooks would run on some renders and not others.
 */
const LoadedCollectionEdit = ({ collection, isSaving, submitError, submitViolations, onSave, onCancel }: LoadedCollectionEditProps) => {
    const { values, setValues, baseline, generation, apply } = useJsonHatch(
        formFromCollection(collection),
        document => formFromCollection(document as StacCollection),
        collection
    );

    return (
        <DocumentModes noun='dataset' buildDocument={() => toCollectionDocument(values, baseline as StacCollection)} onApply={apply}>
            <CollectionForm
                key={generation}
                mode='edit'
                initialValues={values}
                isSaving={isSaving}
                submitError={submitError}
                submitViolations={submitViolations}
                onValuesChange={setValues}
                onSubmit={next => onSave(toCollectionDocument(next, baseline as StacCollection))}
                onCancel={onCancel}
            />
        </DocumentModes>
    );
};

/**
 * Correcting a dataset's own record. The engine takes a whole document on `PUT`, so
 * the stored one is the base and the form edits it — anything the form does not show
 * travels back untouched rather than being dropped.
 */
const StacCollectionEdit = () => {
    const { collectionId = '' } = useParams<{ collectionId: string }>();
    const navigate = useNavigate();
    const { data: collection, isLoading } = useGetStacCollectionQuery(collectionId);
    const [updateCollection, { isLoading: isSaving }] = useUpdateStacCollectionMutation();
    const [submitError, setSubmitError] = useState<string | undefined>();
    /** Where the engine says the document is wrong, so the form can mark those inputs. */
    const [submitViolations, setSubmitViolations] = useState<SchemaViolation[]>([]);

    const save = async (next: StacCollection) => {
        setSubmitError(undefined);
        setSubmitViolations([]);

        try {
            await updateCollection(next).unwrap();
            navigate(`/admin/catalogue/${collectionId}`);
        } catch (error) {
            setSubmitError(engineErrorMessage(error as EngineError));
            setSubmitViolations(asEngineViolations(error as EngineError));
        }
    };

    const mainContent = (() => {
        if (isLoading || !collection) {
            return (
                <div className='flex justify-center py-24' data-testid='stac-collection-edit-loading'>
                    <Loader />
                </div>
            );
        }

        return (
            <LoadedCollectionEdit
                collection={collection}
                isSaving={isSaving}
                submitError={submitError}
                submitViolations={submitViolations}
                onSave={save}
                onCancel={() => navigate(`/admin/catalogue/${collectionId}`)}
            />
        );
    })();

    return <StandardAppLayout title={`Edit ${collection?.title || collectionId}`} mainContent={mainContent} />;
};

export default StacCollectionEdit;
